import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { EntityService } from '../../core/entity/entity.service';
import { AuthService } from '../../core/auth/auth.service';
import { roleLabel, roleTooltip } from '../../core/auth/role-labels';
import { Context, ContextType } from '../../core/entity/entity.types';
import { IconComponent, IconName } from '../../shared/icon/icon';
import { StatusChipComponent } from '../../shared/status-chip/status-chip';

interface OnboardingStep {
  readonly number: number;
  readonly icon: IconName;
  readonly title: string;
  readonly description: string;
}

const CONTEXT_TYPE_LABELS: Record<string, string> = {
  entity: 'Business',
  agent: 'Agent',
  system: 'System',
};

const CONTEXT_TYPE_TOOLTIPS: Record<string, string> = {
  agent: 'A broker or financial advisor account',
};

/** Leading icon shown in the card avatar, per context type. */
const CONTEXT_TYPE_ICONS: Record<ContextType, IconName> = {
  entity: 'building',
  agent: 'users',
  system: 'shield',
};

@Component({
  selector: 'app-dashboard',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTooltipModule,
    RouterLink,
    IconComponent,
    StatusChipComponent,
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Contexts read from the shared service cache so the view reacts to changes
   * made elsewhere (e.g. the dev "Switch User" action refreshing the cache).
   * `null` means "not yet loaded" — drives the loading state below.
   */
  protected readonly contexts = this.entityService.contexts;
  protected readonly isLoadingContexts = computed(() => this.contexts() === null);
  protected readonly contextsError = signal(false);

  /** Time-of-day greeting, e.g. "Good morning". */
  protected readonly greeting = computed(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  });

  protected readonly firstName = computed(() => this.authService.currentUser()?.first_name ?? null);

  /**
   * Contexts sorted so anything needing the owner floats to the top, then
   * entities, then agent/system contexts. Stable within each group.
   */
  protected readonly sortedContexts = computed<Context[]>(() => {
    const ctxs = this.contexts();
    if (!ctxs) return [];
    return [...ctxs]
      .map((c, i) => ({ c, i }))
      .sort((a, b) => {
        const ua = a.c.funding?.needs_attention ? 0 : 1;
        const ub = b.c.funding?.needs_attention ? 0 : 1;
        if (ua !== ub) return ua - ub;
        return a.i - b.i;
      })
      .map(({ c }) => c);
  });

  protected readonly entityContexts = computed(() =>
    (this.contexts() ?? []).filter((c) => c.type === 'entity'),
  );

  /** Three at-a-glance numbers above the cards (entity contexts only). */
  protected readonly stats = computed(() => {
    const entities = this.entityContexts();
    const active = entities.filter((c) => c.funding?.tone === 'positive').length;
    const pending = entities.filter((c) => c.funding?.needs_attention).length;
    return { businesses: entities.length, active, pending };
  });

  /** Show the stat strip only when the user actually has businesses. */
  protected readonly showStats = computed(() => this.entityContexts().length > 0);

  /** Number of soft-deleted businesses the user can restore. */
  protected readonly deletedCount = signal(0);
  protected readonly hasDeletedBusinesses = computed(() => this.deletedCount() > 0);

  /** The three steps shown to a user who has no businesses yet. */
  protected readonly onboardingSteps: readonly OnboardingStep[] = [
    {
      number: 1,
      icon: 'building',
      title: 'Register your business',
      description: 'Tell us about your company so we can tailor funding options to your needs.',
    },
    {
      number: 2,
      icon: 'document',
      title: 'Initiate an application',
      description: 'Start an application and let our AI guide you through what you need.',
    },
    {
      number: 3,
      icon: 'chart',
      title: 'Get your funding results',
      description: 'Receive an AI-assisted credit decision and your funding options.',
    },
  ];

  protected getContextTypeLabel(type: string): string {
    return CONTEXT_TYPE_LABELS[type] ?? type;
  }

  protected getContextTypeTooltip(type: string): string {
    return CONTEXT_TYPE_TOOLTIPS[type] ?? '';
  }

  protected readonly getRoleLabel = roleLabel;

  protected readonly getRoleTooltip = roleTooltip;

  protected getContextIcon(type: ContextType): IconName {
    return CONTEXT_TYPE_ICONS[type] ?? 'building';
  }

  /** CSS modifier for the funding eyebrow tone. */
  protected fundingToneClass(tone: string | undefined): string {
    return tone ? `context-card__funding--${tone}` : '';
  }

  /** Human-friendly "trading since" label, e.g. "Trading since Mar 2021". */
  protected getTradingSince(iso: string | null | undefined): string | null {
    if (!iso) {
      return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const formatted = date.toLocaleDateString('en-ZA', {
      month: 'short',
      year: 'numeric',
    });
    return `Trading since ${formatted}`;
  }

  protected reload(): void {
    this.contextsError.set(false);
    this.entityService
      .getContexts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.contextsError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected navigateTo(url: string): void {
    void this.router.navigateByUrl(url);
  }

  ngOnInit(): void {
    // Refresh the shared context cache; the view binds to it reactively, so the
    // template updates automatically when it lands. We subscribe here only to
    // surface a load error in the UI.
    this.entityService
      .getContexts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // When the user only has a single context (one business, agent, or
        // system role), skip the chooser and drop them straight into it.
        next: (res) => {
          const ctxs = res.contexts;
          if (ctxs.length === 1) {
            void this.router.navigateByUrl(ctxs[0].url, { replaceUrl: true });
          }
        },
        error: () => {
          this.contextsError.set(true);
          this.cdr.markForCheck();
        },
      });

    // Only surface the "Deleted businesses" link when there's something to restore.
    this.entityService
      .getDeletedEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.deletedCount.set(res.entities.length);
          this.cdr.markForCheck();
        },
        // A failure here is non-critical; just leave the link hidden.
        error: () => undefined,
      });
  }
}
